import { scopedThreadKey, scopeThreadRef } from "@cmmd-forge/client-runtime";
import { type EnvironmentId, type ScopedThreadRef, type ThreadId } from "@cmmd-forge/contracts";
import { useEffect, useMemo } from "react";
import { usePrimaryEnvironmentId } from "../../environments/primary";
import { useSavedEnvironmentRuntimeStore } from "../../environments/runtime/savedEnvironmentRuntimeStore";
import { useServerConfig } from "~/rpc/serverState";
import { useStore } from "../../store";
import {
  createThreadSelectorByRef,
  createThreadSubscriptionErrorSelector,
} from "../../storeSelectors";
import { useUiStateStore } from "../../uiStateStore";
import { selectThreadTerminalState, useTerminalStateStore } from "../../terminalStateStore";
import { useComposerDraftStore } from "../../composerDraftStore";
import { type DraftId } from "../../draftId";
import { DEFAULT_INTERACTION_MODE, DEFAULT_RUNTIME_MODE } from "../../types";
import {
  buildLocalDraftThread,
  deriveChatViewRouteState,
  deriveDraftThreadProviderState,
  deriveLocalDraftError,
} from "../ChatView.logic";
import { deriveHydratedServerThreadStatus } from "./serverThreadStatus";
import { createThreadDetailHydratedSelector } from "../../threadDetailHydration";
import { useMountedTerminalThreadRefs } from "./useMountedTerminalThreadRefs";
import { useChatThreadErrorState } from "./useChatThreadErrorState";
import { useDraftRouteProviderBootstrap } from "./draftRouteProviderBootstrap";
import { type UnifiedSettings } from "@cmmd-forge/contracts/settings";
import { canonicalComposerSubmissionAcknowledgements } from "./canonicalComposerSubmissionAcknowledgements";
import { reconcileComposerSubmissionAcknowledgement } from "./reconcileRemoteComposerSubmission";

export function useChatRouteBootstrap(input: {
  readonly environmentId: EnvironmentId;
  readonly threadId: ThreadId;
  readonly routeKind: "server" | "draft";
  readonly draftId: DraftId | null;
  readonly draftRouteId: DraftId | null;
  readonly preferredModelSelection: UnifiedSettings["textGenerationModelSelection"];
}) {
  const routeThreadRef = useMemo(
    () => scopeThreadRef(input.environmentId, input.threadId),
    [input.environmentId, input.threadId],
  );
  const routeThreadKey = useMemo(() => scopedThreadKey(routeThreadRef), [routeThreadRef]);
  const composerDraftTarget: ScopedThreadRef | DraftId =
    input.routeKind === "server" ? routeThreadRef : (input.draftRouteId ?? input.draftId!);
  const serverThread = useStore(
    useMemo(
      () => createThreadSelectorByRef(input.routeKind === "server" ? routeThreadRef : null),
      [input.routeKind, routeThreadRef],
    ),
  );
  const serverThreadSubscriptionError = useStore(
    useMemo(
      () =>
        createThreadSubscriptionErrorSelector(input.routeKind === "server" ? routeThreadRef : null),
      [input.routeKind, routeThreadRef],
    ),
  );
  const serverThreadDetailHydrated = useStore(
    useMemo(
      () =>
        createThreadDetailHydratedSelector(input.routeKind === "server" ? routeThreadRef : null),
      [input.routeKind, routeThreadRef],
    ),
  );
  const setStoreThreadError = useStore((store) => store.setError);
  const { localDraftErrorsByDraftId, setThreadError } = useChatThreadErrorState({
    draftId: input.draftId,
    routeThreadRef,
    serverThread,
    setStoreThreadError,
  });
  const markThreadVisited = useUiStateStore((store) => store.markThreadVisited);
  const activeThreadLastVisitedAt = useUiStateStore((store) =>
    input.routeKind === "server" ? store.threadLastVisitedAtById[routeThreadKey] : undefined,
  );
  const composerRuntimeMode = useComposerDraftStore(
    (store) => store.getComposerDraft(composerDraftTarget)?.runtimeMode ?? null,
  );
  const composerInteractionMode = useComposerDraftStore(
    (store) => store.getComposerDraft(composerDraftTarget)?.interactionMode ?? null,
  );
  const composerActiveProvider = useComposerDraftStore(
    (store) => store.getComposerDraft(composerDraftTarget)?.activeProvider ?? null,
  );
  const composerActiveModelSelection = useComposerDraftStore((store) => {
    const composerDraft = store.getComposerDraft(composerDraftTarget);
    const activeProvider = composerDraft?.activeProvider;
    return activeProvider
      ? (composerDraft?.modelSelectionByProvider[activeProvider] ?? null)
      : null;
  });
  const composerPendingAutoSubmit = useComposerDraftStore(
    (store) => store.getComposerDraft(composerDraftTarget)?.pendingAutoSubmit ?? false,
  );
  const composerStagedSubmission = useComposerDraftStore(
    (store) => store.getComposerDraft(composerDraftTarget)?.stagedSubmission ?? null,
  );
  const canonicalComposerAcknowledgements = useMemo(
    () =>
      canonicalComposerSubmissionAcknowledgements({
        canonicalMessages: serverThread?.messages,
      }),
    [serverThread?.messages],
  );
  useEffect(() => {
    if (input.routeKind !== "server") return;
    reconcileComposerSubmissionAcknowledgement(routeThreadRef, canonicalComposerAcknowledgements);
  }, [canonicalComposerAcknowledgements, input.routeKind, routeThreadRef]);
  const draftThread = useComposerDraftStore((store) =>
    input.routeKind === "server"
      ? store.getDraftSessionByRef(routeThreadRef)
      : input.draftId
        ? store.getDraftSession(input.draftId)
        : null,
  );
  const terminalState = useTerminalStateStore((state) =>
    selectThreadTerminalState(state.terminalStateByThreadKey, routeThreadRef),
  );
  const terminalStateByThreadKey = useTerminalStateStore((state) => state.terminalStateByThreadKey);
  const environmentStateById = useStore((state) => state.environmentStateById);
  const primaryEnvironmentId = usePrimaryEnvironmentId();
  const draftThreadsByThreadKey = useComposerDraftStore((store) => store.draftThreadsByThreadKey);
  const draftThreadEnvironmentId = draftThread?.environmentId;
  const draftRouteProviderBootstrap = useDraftRouteProviderBootstrap({
    routeKind: input.routeKind,
    draftEnvironmentId: draftThreadEnvironmentId,
  });
  const primaryServerConfig = useServerConfig();
  const activeEnvironmentIdForServerConfig =
    serverThread?.environmentId ?? draftThreadEnvironmentId;
  const activeEnvRuntimeState = useSavedEnvironmentRuntimeStore((s) =>
    activeEnvironmentIdForServerConfig ? s.byId[activeEnvironmentIdForServerConfig] : null,
  );
  const { providerStatuses, defaultDraftModelSelection } = useMemo(
    () =>
      deriveDraftThreadProviderState({
        activeEnvironmentId: activeEnvironmentIdForServerConfig,
        primaryEnvironmentId,
        primaryServerConfig,
        activeEnvironmentServerConfig: activeEnvRuntimeState?.serverConfig ?? null,
        draftRouteProviderStatuses: draftRouteProviderBootstrap.draftRouteProviderStatuses,
        preferredModelSelection: composerActiveModelSelection ?? input.preferredModelSelection,
      }),
    [
      activeEnvironmentIdForServerConfig,
      activeEnvRuntimeState?.serverConfig,
      composerActiveModelSelection,
      draftRouteProviderBootstrap.draftRouteProviderStatuses,
      input.preferredModelSelection,
      primaryEnvironmentId,
      primaryServerConfig,
    ],
  );
  const localDraftError = deriveLocalDraftError({
    routeKind: input.routeKind,
    serverThread,
    draftId: input.draftId,
    localDraftErrorsByDraftId,
  });
  const localDraftThread = useMemo(
    () =>
      draftThread
        ? buildLocalDraftThread(
            input.threadId,
            draftThread,
            defaultDraftModelSelection,
            localDraftError,
          )
        : undefined,
    [defaultDraftModelSelection, draftThread, input.threadId, localDraftError],
  );
  const { activeThread, canOpenRightSidebar, isLocalDraftThread, isServerThread } =
    deriveChatViewRouteState({
      routeKind: input.routeKind,
      serverThread,
      draftThread: localDraftThread,
    });
  const serverThreadStatus = deriveHydratedServerThreadStatus({
    routeKind: input.routeKind,
    serverThread,
    detailHydrated: serverThreadDetailHydrated,
    subscriptionError: serverThreadSubscriptionError,
  });
  const runtimeMode = composerRuntimeMode ?? activeThread?.runtimeMode ?? DEFAULT_RUNTIME_MODE;
  const interactionMode =
    composerInteractionMode ?? activeThread?.interactionMode ?? DEFAULT_INTERACTION_MODE;
  const activeThreadId = activeThread?.id ?? null;
  const activeThreadEnvironmentId = activeThread?.environmentId;
  const activeThreadProjectId = activeThread?.projectId;
  const activeThreadRef = useMemo(
    () =>
      activeThreadEnvironmentId && activeThreadId
        ? scopeThreadRef(activeThreadEnvironmentId, activeThreadId)
        : null,
    [activeThreadEnvironmentId, activeThreadId],
  );
  const activeThreadKey = activeThreadRef ? scopedThreadKey(activeThreadRef) : null;
  const { mountedTerminalThreadRefs } = useMountedTerminalThreadRefs({
    terminalStateByThreadKey,
    environmentStateById,
    draftThreadsByThreadKey,
    activeThreadKey,
    activeThreadTerminalOpen: Boolean(activeThreadKey && terminalState.terminalOpen),
  });

  return {
    routeThreadRef,
    routeThreadKey,
    composerDraftTarget,
    serverThread,
    serverThreadSubscriptionError,
    setStoreThreadError,
    setThreadError,
    markThreadVisited,
    activeThreadLastVisitedAt,
    composerRuntimeMode,
    composerInteractionMode,
    composerActiveProvider,
    composerPendingAutoSubmit,
    composerStagedSubmission,
    draftThread,
    terminalState,
    providerStatuses,
    activeThread,
    localDraftThread,
    canOpenRightSidebar,
    isLocalDraftThread,
    isServerThread,
    serverThreadStatus,
    runtimeMode,
    interactionMode,
    activeThreadId,
    activeThreadEnvironmentId,
    activeThreadProjectId,
    activeThreadRef,
    activeThreadKey,
    mountedTerminalThreadRefs,
    primaryEnvironmentId,
  };
}
